import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { queryKeys } from '@/lib/query-keys';
import { askPoco } from '@/lib/edge-function-api';
import type { ClientChat, ChatMessage, SendMessageRequest, ChatResponse } from '@/types/chat';

/**
 * Fetch all chats, optionally scoped to a client.
 * When clientId is null, fetches org-wide (global) chats for the current user.
 * When clientId is undefined, the query is disabled.
 */
export function useClientChats(clientId: string | null | undefined) {
  return useQuery({
    queryKey: clientId ? queryKeys.chat.sessions(clientId) : queryKeys.chat.globalSessions(),
    queryFn: async () => {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) return [];

      let query = supabase
        .from('client_chats')
        .select('*')
        .order('updated_at', { ascending: false });

      if (clientId) {
        query = query.eq('client_id', clientId);
      } else {
        query = query.is('client_id', null).eq('created_by', user.id);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data as ClientChat[];
    },
    enabled: clientId !== undefined,
  });
}

/**
 * Fetch messages for a specific chat
 */
export function useChatMessages(chatId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.chat.messages(chatId),
    queryFn: async () => {
      if (!chatId) return [];
      const { data, error } = await supabase
        .from('chat_messages')
        .select('*')
        .eq('chat_id', chatId)
        .order('created_at', { ascending: true });
      if (error) throw error;
      return data as ChatMessage[];
    },
    enabled: !!chatId,
  });
}

/**
 * Send a message to the ask-poco edge function.
 * Supports both client-scoped and global (org-wide) conversations.
 */
export function useSendMessage() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({
      clientId,
      message,
      chatId,
      scope,
    }: SendMessageRequest): Promise<ChatResponse> => {
      return askPoco({ clientId, message, chatId, scope });
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.chat.all });
      queryClient.invalidateQueries({
        queryKey: queryKeys.chat.messages(data.chatId),
      });
    },
    onError: (error: Error) => {
      console.error('[ask-poco] onError handler:', error.message);
      toast({
        title: 'Failed to send message',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

/**
 * Create a new chat for a client
 */
export function useCreateChat() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ clientId, title }: { clientId: string; title: string }) => {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('client_chats')
        .insert({ client_id: clientId, title, created_by: user.id })
        .select()
        .single();

      if (error) throw error;
      return data as ClientChat;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.chat.sessions(data.client_id),
      });
      toast({ title: 'Chat created successfully' });
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to create chat',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

/**
 * Delete a chat.
 * clientId is optional — global chats have no associated client.
 */
export function useDeleteChat() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({
      chatId,
      clientId,
    }: {
      chatId: string;
      clientId?: string;
    }) => {
      const { error } = await supabase.from('client_chats').delete().eq('id', chatId);

      if (error) throw error;
      return { chatId, clientId };
    },
    onSuccess: (data) => {
      if (data.clientId) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.chat.sessions(data.clientId),
        });
      } else {
        queryClient.invalidateQueries({
          queryKey: queryKeys.chat.globalSessions(),
        });
      }
      queryClient.invalidateQueries({
        queryKey: queryKeys.chat.messages(data.chatId),
      });
      toast({ title: 'Chat deleted successfully' });
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to delete chat',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

/**
 * Update a chat's title
 */
export function useUpdateChatTitle() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({
      chatId,
      title,
      clientId,
    }: {
      chatId: string;
      title: string;
      clientId: string;
    }) => {
      const { data, error } = await supabase
        .from('client_chats')
        .update({ title, updated_at: new Date().toISOString() })
        .eq('id', chatId)
        .select()
        .single();

      if (error) throw error;
      return { ...data, clientId } as ClientChat & { clientId: string };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.chat.sessions(data.clientId),
      });
      toast({ title: 'Chat title updated' });
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to update chat title',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}
